import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { InjectorKeys } from '@core/utils/injector.keys';
import { BackendService } from '@core/prototypes/backend.service';
import { AbstractGroupedList } from '@hoc/hoc-grouped-list';
import { Employee, employeeGroupingOperator, EmployeeSkillFilter } from '@core/models/employee';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationConfiguration } from '@core/services/configuration/application.configuration.interface';
import { ListItem } from '@core/models/list-item.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-skill-employee-list',
  templateUrl: './skill-employee-list.component.html',
  standalone: false
})
export class SkillEmployeeListComponent extends AbstractGroupedList<Employee> implements OnChanges {
  @Input() isAdmin = false;
  @Input() stars? = 0;
  @Input() skill? = '';
  @Input() type = 'skill';
  @Input() coreCompetence? = '';
  public override filter: EmployeeSkillFilter = {
    skill: '',
    skillLevel: 0,
    sortDirection: 'asc',
    pageNumber: 0,
    pageSize: 10,
    type: 'skill'
  };

  constructor(
    @Inject(InjectorKeys.EMPLOYEES_SKILL_SERVICE) private service: BackendService<Employee>,
    cd: ChangeDetectorRef,
    router: Router,
    route: ActivatedRoute,
    @Inject(InjectorKeys.CONFIGURATION) public config: ApplicationConfiguration
  ) {
    super(service, employeeGroupingOperator, config, cd, router, route);
    this.filter.pageSize = config.pageSize ?? 10;
    this.filter.skill = this.skill ?? '';
    this.filter.skillLevel = this.stars ?? 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.filter.skill = this.skill ?? '';
    this.filter.skillLevel = this.stars ?? 0;
    this.filter.type = this.type;

    if (Object.keys(changes).length > 0) {
      const filterIsValidForFetch = this.filter.skill.trim().length > 0;
      if (filterIsValidForFetch) {
        this.filter.pageNumber = 0;
        this.reloadList();
      }
    }
    if (
      (changes['coreCompetence'] && changes['coreCompetence'].currentValue) ||
      (changes['sorting'] && changes['sorting'].currentValue)
    ) {
      this.filter.skill = this.coreCompetence ?? '';
      this.sorting = this.sorting as 'asc' | 'desc';
      this.filter.sortDirection = this.sorting;
      this.filter.pageNumber = 0;
      this.reloadList();
    }
  }

  private reloadList(): void {
    const currentSkill = this.skill ?? '';
    const currentStars = this.stars ?? 0;
    const filterIsValidForFetch = currentSkill.trim().length > 0 && currentStars > 0;

    if (filterIsValidForFetch) {
      this.reload();
    }
  }

  public override processFilter(): EmployeeSkillFilter {
    const finalFilter: EmployeeSkillFilter = {
      skill: this.filter.skill,
      skillLevel: this.filter.skillLevel,
      type: this.filter.type,
      sortDirection: this.filter.sortDirection,
      pageNumber: this.filter.pageNumber,
      pageSize: this.filter.pageSize
    };
    return finalFilter;
  }

  getPersonLink(item: Employee): string {
    const coreCompetenceValue = this.skill ? encodeURIComponent(this.skill) : '';
    let tabValue: string = 'profile';
    if (this.type === 'skill') {
      tabValue = 'skills';
    }
    const type = this.type ? encodeURIComponent(this.type) : '';
    return `team?activeOnly=false&pageNumber=0&pageSize=10&searchValue=${item.businessPartnerFormattedName}&sorting=asc&employee=${item.email}&tab=${tabValue}` +
      `&highlightSkill=${coreCompetenceValue}`
  }

  public override sortingChanged(sorting: string): void {
    const newSorting = sorting as 'asc' | 'desc';
    if (this.filter.sortDirection === newSorting) {
      return;
    }
    this.filter.sortDirection = newSorting;
    this.filter.pageNumber = 0;
    this.reload();
  }

  public override gotoPage(pageNumber: number): void {
    if (pageNumber === this.filter.pageNumber || pageNumber < 0) {
      return;
    }
    this.filter.pageNumber = pageNumber;
    this.reload();
  }

  public getTeamListItem(employeeItem: Employee): ListItem {
    let data: ListItem = {
      url: this.getPersonLink(employeeItem),
      columns: [
        {
          image: {
            src: employeeItem.email ?? ''
          }
        },
        {
          lines: [
            {
              content: employeeItem.businessPartnerFormattedName,
              strong: true
            },
            {
              content: employeeItem.roleDescription ?? 'n/a'
            }
          ]
        }
      ]
    };
    return data;
  }
}
